import React from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { StarRating } from "@/components/StarRating";
import { ArrowRight, Clock, Users, Music, PawPrint, MessageCircle, Zap, BadgeCheck } from "lucide-react";

export const RideCard = ({ ride, onSelect, selected }) => {
  const navigate = useNavigate();
  const driver = ride.driver || {};
  const prefs = ride.preferences || {};

  const go = () => navigate(`/rides/${ride.ride_id}`);

  return (
    <Card
      data-testid="ride-result-card"
      onClick={go}
      onMouseEnter={onSelect ? () => onSelect(ride) : undefined}
      className={`glass p-4 cursor-pointer transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-[1px] hover:border-white/20 ${selected ? "ring-2 ring-[rgba(34,211,238,0.45)]" : ""}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm text-secondary-cosmic">
            <Clock size={14} className="text-[#22D3EE]" />
            <span className="font-mono-cosmic">{ride.time}</span>
            <span className="text-white/30">·</span>
            <span className="font-mono-cosmic text-xs">{ride.date}</span>
          </div>
          <div className="mt-1 flex items-center gap-2 font-display text-lg text-white">
            <span className="truncate">{ride.from_city}</span>
            <ArrowRight size={16} className="text-[#7C3AED] shrink-0" />
            <span className="truncate">{ride.to_city}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-semibold text-white">{ride.price_per_seat}<span className="text-sm text-muted-cosmic"> ฿</span></div>
          <div className="text-xs text-muted-cosmic">per seat</div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar className="h-8 w-8 ring-2 ring-[rgba(34,211,238,0.35)]">
            <AvatarImage src={driver.picture} alt={driver.name} />
            <AvatarFallback className="bg-[#3B82F6] text-white text-xs">{(driver.name || "?").slice(0, 2)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              <span className="text-sm text-white truncate">{driver.name}</span>
              {driver.verified_email && <BadgeCheck size={13} className="text-[#22D3EE] shrink-0" />}
            </div>
            <StarRating value={driver.rating || 0} size={12} count={driver.review_count} />
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {ride.instant_booking && (
            <Badge className="bg-[rgba(247,201,72,0.14)] text-[#FFF7D6] border border-[rgba(247,201,72,0.25)] gap-1"><Zap size={11} /> Instant</Badge>
          )}
          <Badge className="bg-[rgba(34,211,238,0.12)] text-[#A5F3FC] border border-[rgba(34,211,238,0.22)] gap-1">
            <Users size={11} /> {ride.seats_available} left
          </Badge>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-2 text-white/50">
        {prefs.music && <Music size={13} title="Music" />}
        {prefs.pets && <PawPrint size={13} title="Pets allowed" />}
        {prefs.chatty === "love" && <MessageCircle size={13} title="Chatty" />}
        {ride.car && <span className="text-xs text-muted-cosmic ml-auto">{ride.car}</span>}
      </div>
    </Card>
  );
};
